import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  FREE_PLAN_NAME,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_COMPLIANCE_PLAN_NAME,
  getSubscription,
  fossaPlanMap,
  isEnterpriseTier,
} from '../../../../shared/subscriptions';
import { window } from '../../../globals';
import { useToggle } from '../../../hooks';
import SubscriptionStore from '../../../redux/subscriptions';
import { BUTTON_TYPE } from '../../generic/Button/button-constants';
import Loader from '../../generic/Loader';
import ModalDowngradeSubscription from '../../modals/ModalDowngradeSubscription';
import { PaymentModal } from './PaymentComponent';
import { BUSINESS_TIER_FEATURES, ENTERPRISE_TIER_FEATURES, FREE_TIER_FEATURES } from './consts';
import { SubscriptionTierInfo, SubTier } from './SubscriptionTierInfo';
import { isAuthed } from '../../../utils';

import type { CurrentOrgSubscription } from './SubscriptionTierInfo';
import type { FossaPlanName } from '../../../../shared/subscriptions';

// The SubTier enum values are used for comparisons to determine what to render. This is just a helper to line up the
// FOSSA plan names with the appropriate enum values.
export const planNameToSubTier = (planName?: FossaPlanName): SubTier => {
  if (!isAuthed()) {
    return SubTier.UNAUTHED;
  }

  if (window.user?.organization?.custom_billing || isEnterpriseTier(planName)) {
    return SubTier.ENTERPRISE;
  }

  switch (planName) {
    case FREE_PLAN_NAME:
      return SubTier.FREE;
    case LEGACY_PREMIUM_PLAN_1:
    case LEGACY_PREMIUM_PLAN_2:
      return SubTier.LEGACY;
    case PREMIUM_COMPLIANCE_PLAN_NAME:
    case PREMIUM_COMP_SEC_PLAN_NAME:
      return SubTier.BUSINESS;
    default:
      return SubTier.UNAUTHED;
  }
};

export const PlanSelector = () => {
  const dispatch = useDispatch();

  // Convenience variables.
  // Trying to be very explicit here that if the user is unauthed, we want undefined.
  const orgPlan = isAuthed() ? window.user?.organization?.stripe_plan : undefined;
  const isAlreadySecurityPlan = orgPlan === PREMIUM_COMP_SEC_PLAN_NAME;

  const wasUpdated = useSelector(SubscriptionStore.selectors.wasUpdated);
  const subscription = useSelector(SubscriptionStore.selectors.getSubscription);
  const [isSecuritySelected, onSecurityToggle] = useToggle(isAlreadySecurityPlan);

  // If the org is on the free tier or doesn't have a subcsription quantity, default to 5 developers.
  // Otherwise, use the quantity that their currently subscribed for.
  const orgCurrentDevCount =
    orgPlan === FREE_PLAN_NAME ? 5 : subscription?.quantity ?? 5;
  const [devCount, setDevCount] = useState(orgCurrentDevCount);

  const [isPaymentShowing, setIsPaymentShowing] = useState(false);
  const [isDowngradeShowing, setIsDowngradeShowing] = useState(false);
  const [newPlanName, setNewPlanName] = useState<FossaPlanName>(FREE_PLAN_NAME);
  const [currentOrgSub, setCurrentOrgSub] = useState<CurrentOrgSubscription>({
    tier: planNameToSubTier(orgPlan),
    hasSecurity: isAlreadySecurityPlan,
    devCount: 5,
  });

  const businessTierPrice =
    (isSecuritySelected
      ? fossaPlanMap[PREMIUM_COMP_SEC_PLAN_NAME].price
      : fossaPlanMap[PREMIUM_COMPLIANCE_PLAN_NAME].price) * devCount;

  // This is needed because the window.user and window.user.organzation values won't be updated.
  useEffect(() => {
    if (wasUpdated) {
      window.location.reload();
    }
  }, [wasUpdated]);

  useEffect(() => {
    if (!subscription) {
      dispatch(SubscriptionStore.actions.fetchSubscription());
      return;
    }

    // If the org isn't on the free tier, update the devCount once the subscription loads.
    if (subscription && orgPlan !== FREE_PLAN_NAME) {
      setDevCount(subscription.quantity);
      setCurrentOrgSub({
        tier: planNameToSubTier(orgPlan),
        hasSecurity: isAlreadySecurityPlan,
        devCount: subscription.quantity,
      });
    }
  }, [subscription, orgPlan, isAlreadySecurityPlan, dispatch]);

  if (isAuthed() && !subscription) {
    return <Loader />;
  }

  return (
    <div className="plan-selector-container">
      <SubscriptionTierInfo
        name="Free"
        currentOrgSub={currentOrgSub}
        price="Free"
        panelTier={SubTier.FREE}
        featureList={FREE_TIER_FEATURES}
        buttonOptions={{
          title: 'FreeTierButton',
          onClick: () => setIsDowngradeShowing(true),
        }}
      />

      <SubscriptionTierInfo
        name="Business"
        panelTier={SubTier.BUSINESS}
        currentOrgSub={currentOrgSub}
        price={businessTierPrice}
        featureList={BUSINESS_TIER_FEATURES}
        buttonOptions={{
          title: 'BusinessTierButton',
          onClick: () => {
            const premiumPlanName = isSecuritySelected
              ? PREMIUM_COMP_SEC_PLAN_NAME
              : PREMIUM_COMPLIANCE_PLAN_NAME;
            setNewPlanName(premiumPlanName);
            setIsPaymentShowing(true);
          },
        }}
        securityOptions={{
          isSelected: isSecuritySelected,
          onToggle: onSecurityToggle,
        }}
        devCountOptions={{
          devCount,
          onChange: setDevCount,
        }}
      />

      <SubscriptionTierInfo
        name="Enterprise"
        panelTier={SubTier.ENTERPRISE}
        currentOrgSub={currentOrgSub}
        price="Custom Pricing"
        featureList={ENTERPRISE_TIER_FEATURES}
        buttonOptions={{
          title: 'Choose Plan',
          text: 'Contact Us',
          type: BUTTON_TYPE.DARK,
          onClick: () => {
            window.open('https://fossa.com/request-demo');
          },
        }}
      />
      <PaymentModal
        show={isPaymentShowing}
        onHide={() => {
          setIsPaymentShowing(false);
        }}
        newPlan={getSubscription(newPlanName)}
        developers={devCount}
      />
      <ModalDowngradeSubscription
        isShowing={isDowngradeShowing}
        onHide={() => setIsDowngradeShowing(false)}
        orgCurrPlan={orgPlan || FREE_PLAN_NAME}
      />
    </div>
  );
};
